import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, LayoutGroup, useReducedMotion } from 'motion/react'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { useKeys } from 'services/KeysProvider'
import { EVENT_NAMES } from 'services/Analytics/Analytics'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import type { Holiday } from 'services/HolidayService'
import type { CartItem } from 'stores/cartStore/CartItem'
import { useBagAnalytics } from '../../hooks/useBagAnalytics'
import { ReactComponent as ChevronDown } from '../../../../icons/assets/ChevronDown.svg'
import { formatPrice } from '../../../../utils/utils'
import type { GroupPricingProduct } from '../../groupCheckoutPricing.types'
import { QUANTITY_CONTROL_MODE } from '../../cart.consts'
import { getEditRoute } from '../../cart.utils'
import { generateTitle } from '../../cartItemTitle'
import { useCartItemWarning } from '../../hooks/useCartItemWarning'
import Skeleton from 'components/Skeleton/Skeleton'
import ArrivesBeforeHolidayLine from './ArrivesBeforeHolidayLine'
import ProductAlert from './ProductAlert'
import ProductThumbnail from './ProductThumbnail/ProductThumbnail'
import SizeBreakdown from './SizeBreakdown'
import {
  CART_ENTER_TRANSITION,
  CART_EXIT_TRANSITION,
  CART_LAYOUT_TRANSITION,
} from './consts'
import QuantityControls from './QuantityControls'
import * as S from './ProductSummaryRow.styles'

const INSTANT_TRANSITION = { duration: 0 } as const

type ProductSummaryRowProps = {
  item: CartItem
  currency?: string
  matchedPricingProducts?: GroupPricingProduct[]
  isPricingLoading?: boolean
  reachableHoliday: Holiday | undefined
  onEdit: (item: CartItem) => void
  onQuantityChange: (item: CartItem, newQuantity: number) => void
  onRemove: (item: CartItem) => void
}

function ProductSummaryRow({
  item,
  currency,
  matchedPricingProducts,
  isPricingLoading,
  reachableHoliday,
  onEdit,
  onQuantityChange,
  onRemove,
}: ProductSummaryRowProps) {
  const { t } = useTranslation()
  const { ipCountry } = useKeys()
  const [isExpanded, setIsExpanded] = useState(false)
  const warning = useCartItemWarning(item)
  const shouldReduceMotion = useReducedMotion()
  const { trackBagItemEvent } = useBagAnalytics()
  const experimentManager = useExperimentManager()
  const isPriceTierListEnabled = experimentManager.isEnabled(
    'pricing_photo_book_price_tier'
  )

  const layoutTransition = shouldReduceMotion
    ? INSTANT_TRANSITION
    : CART_LAYOUT_TRANSITION
  const enterTransition = shouldReduceMotion
    ? INSTANT_TRANSITION
    : CART_ENTER_TRANSITION
  const exitTransition = shouldReduceMotion
    ? INSTANT_TRANSITION
    : CART_EXIT_TRANSITION

  const { title, priceInfo } = useMemo(() => {
    const priceInfo = matchedPricingProducts?.length
      ? {
          originalPrice: matchedPricingProducts.reduce(
            (sum, p) => sum + (p.priceBeforeDiscount ?? 0),
            0
          ),
          discountedPrice: matchedPricingProducts.reduce(
            (sum, p) => sum + (p.priceAfterDiscount ?? 0),
            0
          ),
        }
      : undefined

    return {
      title: generateTitle(item, t, ipCountry, isPriceTierListEnabled),
      priceInfo,
    }
  }, [item, ipCountry, isPriceTierListEnabled, matchedPricingProducts, t])

  const isEditable = getEditRoute(item) !== null
  const quantityControlMode = QUANTITY_CONTROL_MODE[item.productType]
  const isExpandable =
    item.productType === PRODUCT_TYPES.CLASSIC &&
    item.metadata.pricingProducts.length > 1

  // A warning takes the slot first, so a non-dismissible one keeps the holiday line hidden.
  const getItemAlert = () => {
    if (warning) {
      return {
        key: warning.key,
        content: (
          <ProductAlert
            variant="warning"
            dataWarning={warning.key}
            text={warning.text}
            ctaText={warning.ctaText}
            onCtaClick={warning.onFixClick}
            onDismiss={warning.onDismiss}
          />
        ),
      }
    }
    if (reachableHoliday) {
      return {
        key: reachableHoliday.id,
        content: (
          <ArrivesBeforeHolidayLine item={item} holiday={reachableHoliday} />
        ),
      }
    }
    return null
  }
  const itemAlert = getItemAlert()
  const hasContentBelow = !!itemAlert || (isExpandable && isExpanded)
  const hasDiscount =
    priceInfo && priceInfo.originalPrice !== priceInfo.discountedPrice
  let priceContent: React.ReactNode = null
  if (isPricingLoading) {
    priceContent = <Skeleton width={50} />
  } else if (currency && priceInfo) {
    priceContent = (
      <>
        <S.DiscountedPrice data-testid={`cart-item-price-${item.id}`}>
          {formatPrice(priceInfo.discountedPrice, currency)}
        </S.DiscountedPrice>
        {hasDiscount && (
          <S.OriginalPrice data-testid={`cart-item-original-price-${item.id}`}>
            {formatPrice(priceInfo.originalPrice, currency)}
          </S.OriginalPrice>
        )}
      </>
    )
  }

  const handleTitleClick = () => {
    if (!isExpandable) return
    const nextExpanded = !isExpanded
    setIsExpanded(nextExpanded)
    if (nextExpanded) {
      trackBagItemEvent(EVENT_NAMES.CART_PRODUCT_DETAILS_EXPANDED, item, {
        'Product Name': title,
      })
    }
  }

  const handleQuantityChange = (delta: number) => {
    if (delta < 0 && item.quantity <= 1) {
      onRemove(item)
      return
    }
    const quantityBefore = item.quantity
    const quantityAfter = quantityBefore + delta
    onQuantityChange(item, quantityAfter)
    trackBagItemEvent(EVENT_NAMES.CART_QUANTITY_CHANGED, item, {
      'Product Name': title,
      Direction: delta > 0 ? 'increase' : 'decrease',
      'Quantity Before': quantityBefore,
      'Quantity After': quantityAfter,
    })
  }

  return (
    <S.Row
      layout="position"
      transition={layoutTransition}
      data-testid={`cart-item-${item.id}`}
      data-product-type={item.productType}
    >
      <ProductThumbnail item={item} />
      <S.Content>
        <LayoutGroup>
          <S.TopSection
            layout
            $alignTop={hasContentBelow}
            transition={layoutTransition}
          >
            <S.TextContent layout="position" transition={layoutTransition}>
              <S.TitleButton
                $isClickable={isExpandable}
                onClick={handleTitleClick}
                data-testid={`cart-item-title-${item.id}`}
              >
                {title}
                {isExpandable && (
                  <S.Chevron $isExpanded={isExpanded}>
                    <ChevronDown />
                  </S.Chevron>
                )}
              </S.TitleButton>
              {(isEditable || priceContent) && (
                <S.BottomRow>
                  {isEditable && (
                    <S.EditLink
                      onClick={() => {
                        trackBagItemEvent(
                          EVENT_NAMES.CART_PRODUCT_EDIT_TAPPED,
                          item
                        )
                        onEdit(item)
                      }}
                      data-testid={`cart-item-edit-${item.id}`}
                    >
                      {t('order.cart_edit')}
                    </S.EditLink>
                  )}
                  {isEditable && priceContent && <S.Separator>|</S.Separator>}
                  {priceContent}
                </S.BottomRow>
              )}
            </S.TextContent>
            {quantityControlMode && (
              <QuantityControls
                mode={quantityControlMode}
                quantity={item.quantity}
                onDecrement={() => handleQuantityChange(-1)}
                onIncrement={() => handleQuantityChange(1)}
                onDisabledIncrementTap={() =>
                  trackBagItemEvent(
                    EVENT_NAMES.CART_DISABLED_INCREMENT_TAPPED,
                    item,
                    { 'Product Name': title }
                  )
                }
              />
            )}
          </S.TopSection>

          {isExpandable && (
            <S.BreakdownWrapper layout="position" transition={layoutTransition}>
              <SizeBreakdown
                item={item}
                pricingProducts={matchedPricingProducts}
                currency={currency}
                isExpanded={isExpanded}
                isPricingLoading={isPricingLoading}
              />
            </S.BreakdownWrapper>
          )}

          <S.AlertContainer>
            <AnimatePresence initial={false} mode="popLayout">
              {itemAlert && (
                <S.AlertSlot
                  key={`${item.id}-${itemAlert.key}`}
                  variants={{
                    initial: { y: '-100%', opacity: 0 },
                    animate: {
                      y: 0,
                      opacity: 1,
                      transition: enterTransition,
                    },
                    exit: {
                      y: '-100%',
                      opacity: 0,
                      transition: exitTransition,
                    },
                  }}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  {itemAlert.content}
                </S.AlertSlot>
              )}
            </AnimatePresence>
          </S.AlertContainer>
        </LayoutGroup>
      </S.Content>
    </S.Row>
  )
}

export default ProductSummaryRow
